// useDeckSuggestions — Synergie-Vorschläge eines virtuellen Decks, seitenweise.
// Der Query-Key ist normalisiert: gleiche Wirkung → gleicher Key, damit zwei Aufrufer
// mit unterschiedlich ausgeschriebenen Parametern dieselbe Antwort teilen statt zwei
// Anfragen auszulösen (Brücken-Sektion und Erstansicht des Reiters).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { buildCardSearchParams } from '../hooks/useCardBrowserSearch';

/** Wurzel-Key aller Vorschlags-Abfragen — Karten-Mutationen invalidieren darüber. */
export const DECK_SUGGESTIONS_ROOT_KEY = 'deck-suggestions';

/** Seitengröße einer Vorschlags-Seite (Server-Default). */
export const SUGGESTIONS_PAGE_SIZE = 20;

const FILTER_DEBOUNCE_MS = 300;

/**
 * Filter-Anteil in kanonischer Form: die Query-Zeichenkette der Kartensuche ohne
 * Sortierung, Gruppierung und Seitenwerte (die Reihenfolge IST die Rangliste, Seiten
 * gehören nicht in den Key). Leere Filter ergeben `null` — ein leer ausgefülltes
 * Filter-Objekt ist damit derselbe Key wie gar kein Filter.
 */
export function suggestionFilterQuery(filters) {
  const params = buildCardSearchParams(
    { ...(filters || {}), ownedOnly: false, sortOption: '', sortDirection: '', groupBy: '' },
    { limit: 0, offset: 0 },
  );
  params.delete('limit');
  params.delete('offset');
  params.sort();
  const query = params.toString();
  return query === '' ? null : query;
}

/**
 * Parameter des Hooks auf eine kanonische Form bringen. `ownedOnly` gilt als gesetzt,
 * wenn es als eigene Option ODER im Filter-Objekt steht — die Filterzeile trägt den
 * Umschalter im Filter-Objekt, die Brücken-Sektion kennt nur die Option.
 */
export function normalizeSuggestionParams({ filters, ownedOnly, limit } = {}) {
  const rohLimit = Number(limit);
  return {
    filters: suggestionFilterQuery(filters),
    ownedOnly: ownedOnly === true || filters?.ownedOnly === true,
    limit: Number.isFinite(rohLimit) && rohLimit > 0 ? Math.floor(rohLimit) : SUGGESTIONS_PAGE_SIZE,
  };
}

/**
 * Query-Key aus BEREITS normalisierten Parametern. Die einzige Stelle, die die Form des
 * Keys kennt — der Hook baut seinen Key hierüber, damit es keine zweite Fassung gibt, die
 * unbemerkt auseinanderläuft.
 */
export function suggestionKeyFor(deckId, normalized) {
  return [DECK_SUGGESTIONS_ROOT_KEY, deckId ?? null, normalized];
}

/**
 * Query-Key einer Vorschlags-Abfrage aus den ROHEN Hook-Parametern (`{ filters, ownedOnly,
 * limit }` in der Form, in der ein Aufrufer sie übergibt — `filters` ist das Filter-Objekt,
 * nicht die normalisierte Query-Zeichenkette). `deckSuggestionsQueryKey(id, {})` liefert
 * exakt den Key von `deckSuggestionsQueryKey(id, { filters: undefined, ownedOnly: false,
 * limit: 20 })`. Der Offset steckt NICHT im Key — er ist Seiten-Parameter von
 * `useInfiniteQuery`.
 */
export function deckSuggestionsQueryKey(deckId, params) {
  return suggestionKeyFor(deckId, normalizeSuggestionParams(params));
}

/** Query-Zeichenkette einer konkreten Seite. */
export function buildSuggestionQuery({ filters, ownedOnly, limit }, offset) {
  const params = new URLSearchParams(filters || '');
  if (ownedOnly) params.set('ownedOnly', 'true');
  params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  return params.toString();
}

/**
 * Merkt an der geladenen Seite, ob sie voll war. Die Länge der Einträge taugt dafür nur
 * im Moment des Ladens: eine optimistische Entnahme (Karte hinzugefügt, Karte
 * ausgeblendet) kürzt sie, und das Nachladen endete plötzlich, obwohl der Server
 * weiterhin eine nächste Seite hat.
 */
export function markPageFull(seite, limit) {
  if (!seite) return seite;
  return { ...seite, pageWasFull: (seite.entries?.length ?? 0) >= limit };
}

/**
 * Nächster Offset: es gibt nur dann eine weitere Seite, wenn die letzte voll war —
 * eine kürzere Seite ist das Ende des Kandidatenkreises. Maßgeblich ist der Vermerk aus
 * `markPageFull`; ohne ihn (fremd befüllter Cache) zählt die Seitenlänge.
 */
export function nextSuggestionOffset(lastPage, offset, limit) {
  const voll = typeof lastPage?.pageWasFull === 'boolean'
    ? lastPage.pageWasFull
    : (lastPage?.entries?.length ?? 0) >= limit;
  return voll ? offset + limit : undefined;
}

/**
 * Entfernt einen Eintrag aus ALLEN Seiten einer zwischengespeicherten Vorschlags-Antwort.
 * Trifft die Kennung nichts, kommt das Objekt unverändert zurück — React Query rendert
 * dann auch nichts neu. `id` ist die Scryfall-ID des gezeigten Drucks, `oracleId` die
 * Karte über alle Drucke hinweg; angegeben wird, was der Aufrufer kennt.
 *
 * `scoredCount` und `profile` der ersten Seite bleiben stehen: sie beschreiben die
 * Anfrage, nicht die Seite — der Refetch liefert die neuen Werte.
 */
export function removeSuggestionEntry(data, { id, oracleId } = {}) {
  if (!data?.pages || (!id && !oracleId)) return data;
  let getroffen = false;
  const seiten = data.pages.map((seite) => {
    if (!Array.isArray(seite?.entries)) return seite;
    const übrig = seite.entries.filter(
      (eintrag) => !((id && eintrag.id === id) || (oracleId && eintrag.oracleId === oracleId)),
    );
    if (übrig.length === seite.entries.length) return seite;
    getroffen = true;
    return { ...seite, entries: übrig };
  });
  return getroffen ? { ...data, pages: seiten } : data;
}

/**
 * Dieselbe Entnahme über alle Filter-Varianten eines Decks (Prefix-Treffer auf
 * Wurzel-Key + Deck-ID) — die Brücken-Sektion und der Reiter halten getrennte Einträge
 * derselben Rangliste.
 */
export function dropSuggestionFromCache(queryClient, deckId, kennung) {
  queryClient.setQueriesData(
    { queryKey: [DECK_SUGGESTIONS_ROOT_KEY, deckId ?? null] },
    (data) => removeSuggestionEntry(data, kennung),
  );
}

/**
 * Entnahme-Funktion für einen Zweig, der gerade eine Karte hinzugefügt oder ausgeblendet
 * hat: die Kachel verschwindet mit der Server-Bestätigung, statt bis zum Refetch stehen
 * zu bleiben. Mehrfach für dieselbe Karte aufgerufen bleibt sie wirkungslos — beide
 * Hinzufügen-Wege (Katalog und Brücke) dürfen sie deshalb unabhängig voneinander rufen.
 */
export function useDropSuggestionFromCache(deckId) {
  const queryClient = useQueryClient();
  return useCallback(
    (kennung) => dropSuggestionFromCache(queryClient, deckId, kennung),
    [queryClient, deckId],
  );
}

/**
 * Vorschläge eines Decks.
 *
 *   const { profile, entries, scoredCount, available, … } = useDeckSuggestions(deckId, {
 *     filters, ownedOnly, limit,
 *   });
 *
 * Ein Debounce über die gesamten Parameter (nicht je Feld) hält Tipp-Eingaben zusammen;
 * die Vorschlags-Anfrage scort den kompletten Kandidatenkreis und ist teuer.
 * `profile`, `scoredCount` und `available` stammen aus der ersten Seite — sie beschreiben
 * die Anfrage, nicht die Seite.
 */
export function useDeckSuggestions(deckId, params = {}) {
  const zielKey = JSON.stringify(normalizeSuggestionParams(params));
  const [debouncedKey, setDebouncedKey] = useState(zielKey);

  useEffect(() => {
    if (debouncedKey === zielKey) return undefined;
    const timer = setTimeout(() => setDebouncedKey(zielKey), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [zielKey, debouncedKey]);

  const aktiv = useMemo(() => JSON.parse(debouncedKey), [debouncedKey]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: suggestionKeyFor(deckId, aktiv),
    queryFn: async ({ pageParam = 0, signal }) => markPageFull(
      await json(`/api/decks/${deckId}/suggestions?${buildSuggestionQuery(aktiv, pageParam)}`, { signal }),
      aktiv.limit,
    ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => nextSuggestionOffset(lastPage, lastPageParam, aktiv.limit),
    enabled: deckId != null,
  });

  const seiten = useMemo(() => data?.pages || [], [data]);
  const entries = useMemo(() => seiten.flatMap((seite) => seite.entries || []), [seiten]);

  return {
    profile: seiten[0]?.profile || [],
    entries,
    scoredCount: seiten[0]?.scoredCount ?? 0,
    available: seiten[0] ? seiten[0].available !== false : true,
    fetchNextPage,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    status,
    error,
    refetch,
  };
}

export default useDeckSuggestions;
